import { useState } from "react";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { KPIsListResult } from "../api/client";
import { useKPIsQuery } from "../api/queries";

type DepartmentItem = { id: string; name: string };

interface KPIAssignmentFilterProps {
  kpiId?: string;
  assignmentType?: "employee" | "position" | "department" | "all";
  isActive?: boolean | "all";
  departmentId?: string;
  hideKpiFilter?: boolean;
  onFilterChange: (filters: {
    kpiId?: string;
    assignmentType?: "employee" | "position" | "department";
    isActive?: boolean;
    departmentId?: string;
  }) => void;
}

export function KPIAssignmentFilter({
  kpiId,
  assignmentType,
  isActive,
  departmentId,
  hideKpiFilter = false,
  onFilterChange,
}: KPIAssignmentFilterProps) {
  const [localKpiId, setLocalKpiId] = useState(kpiId || "");
  const [localAssignmentType, setLocalAssignmentType] = useState<
    "employee" | "position" | "department" | "all"
  >(assignmentType || "all");
  const [localIsActive, setLocalIsActive] = useState<boolean | "all">(
    isActive ?? "all",
  );
  const [localDepartmentId, setLocalDepartmentId] = useState(
    departmentId || "",
  );

  const { data: kpisData } = useKPIsQuery({ limit: 100 });
  const kpiOptions = (kpisData as KPIsListResult | undefined)?.data ?? [];

  const handleKPIChange = (value: string) => {
    setLocalKpiId(value);
    onFilterChange({
      kpiId: value || undefined,
      assignmentType:
        localAssignmentType === "all" ? undefined : localAssignmentType,
      isActive: localIsActive === "all" ? undefined : localIsActive,
      departmentId: localDepartmentId || undefined,
    });
  };

  const handleAssignmentTypeChange = (
    value: "employee" | "position" | "department" | "all",
  ) => {
    setLocalAssignmentType(value);
    onFilterChange({
      kpiId: localKpiId || undefined,
      assignmentType: value === "all" ? undefined : value,
      isActive: localIsActive === "all" ? undefined : localIsActive,
      departmentId: localDepartmentId || undefined,
    });
  };

  const handleIsActiveChange = (value: boolean | "all") => {
    setLocalIsActive(value);
    onFilterChange({
      kpiId: localKpiId || undefined,
      assignmentType:
        localAssignmentType === "all" ? undefined : localAssignmentType,
      isActive: value === "all" ? undefined : value,
      departmentId: localDepartmentId || undefined,
    });
  };

  const handleDepartmentChange = (value: string) => {
    setLocalDepartmentId(value);
    onFilterChange({
      kpiId: localKpiId || undefined,
      assignmentType:
        localAssignmentType === "all" ? undefined : localAssignmentType,
      isActive: localIsActive === "all" ? undefined : localIsActive,
      departmentId: value || undefined,
    });
  };

  return (
    <div
      className="flex flex-wrap gap-4 border-b bg-card p-4"
      data-tourid="assignment-filter"
    >
      {!hideKpiFilter && (
        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="kpi-filter"
            className="mb-2 block font-medium text-sm"
          >
            KPI
          </label>
          <SimpleSelect
            value={localKpiId || "all"}
            onValueChange={(val) => handleKPIChange(val === "all" ? "" : val)}
            placeholder="ທັງໝົດ"
            className="w-full"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              ...kpiOptions.map((kpi) => ({
                value: kpi.id,
                label: kpi.name,
              })),
            ]}
          />
        </div>
      )}

      <div className="w-[180px]">
        <label
          htmlFor="assignment-type-filter"
          className="mb-2 block font-medium text-sm"
        >
          ປະເພດ
        </label>
        <SimpleSelect
          value={localAssignmentType}
          onValueChange={handleAssignmentTypeChange}
          className="w-full"
          options={[
            { value: "all", label: "ທັງໝົດ" },
            { value: "employee", label: "ພະນັກງານ" },
            { value: "position", label: "ຕໍາແໜ່ງ" },
            { value: "department", label: "ພະແນກ" },
          ]}
        />
      </div>

      <div className="w-[150px]">
        <label
          htmlFor="is-active-filter"
          className="mb-2 block font-medium text-sm"
        >
          ສະຖານະ
        </label>
        <SimpleSelect
          value={String(localIsActive)}
          className="w-full"
          onValueChange={(v) =>
            handleIsActiveChange(v === "all" ? "all" : v === "true")
          }
          options={[
            { value: "all", label: "ທັງໝົດ" },
            { value: "true", label: "ກຳລັງໃຊ້ງານ" },
            { value: "false", label: "ປິດການໃຊ້ງານ" },
          ]}
        />
      </div>

      <div className="min-w-[200px] flex-1">
        <label
          htmlFor="department-filter"
          className="mb-2 block font-medium text-sm"
        >
          ພະແນກ
        </label>
        <div id="department-filter">
          <InfiniteCombobox<DepartmentItem>
            value={localDepartmentId}
            onValueChange={handleDepartmentChange}
            queryKey={["departments-lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ທັງໝົດ"
            clearable
          />
        </div>
      </div>
    </div>
  );
}
